'use client'

import * as React from 'react'
import {
  Command as CommandPrimitive,
  useCommandState,
  defaultFilter as defaultCommandFilter,
} from 'cmdk'

import { CheckIcon, SearchIcon } from 'lucide-react'
import { cn } from '@joyful/utils'
import type { DropdownMenuItemProps } from './DropdownMenu'
import { Ellipsis } from './Ellipsis'
import { inputVariants } from './Input'
import { itemVariants } from './Item'
import { Separator } from './Separator'
import { Slot } from './Slot'
import type { VariantProps } from '@joyful/utils'
import type {
  ComponentProps,
  ComponentRef,
  ReactElement,
  ReactNode,
} from 'react'
export type CommandProps = ComponentProps<typeof CommandPrimitive>
export type CommandRef = ComponentRef<typeof CommandPrimitive>

export { CommandPrimitive }

export const commandItemScopeSeparator = '\u001F'

const CommandGroupScopeContext = React.createContext<string | null>(null)

function filterIgnoringItemScope(
  value: string,
  search: string,
  keywords?: Array<string>,
) {
  const [searchableValue = ''] = value.split(commandItemScopeSeparator)
  return defaultCommandFilter(searchableValue, search, keywords)
}

export function Command({
  className,
  filter = filterIgnoringItemScope,
  ...props
}: CommandProps) {
  return (
    <CommandPrimitive
      filter={filter}
      className={cn(
        'bg-tremor-background text-tremor-content-strong flex size-full min-h-0 flex-col overflow-hidden rounded-md',
        className,
      )}
      {...props}
    />
  )
}

export interface CommandHeaderContentProps extends ComponentProps<'div'> {
  children: ReactNode
}

export function CommandHeaderContent({
  children,
  className,
  ...props
}: CommandHeaderContentProps) {
  return (
    <div className={cn('min-w-0 flex-[1_0_0]', className)} {...props}>
      {children}
    </div>
  )
}

export interface CommandHeaderProps extends ComponentProps<'header'> {
  endAdornment?: ReactNode
  startAdornment?: ReactNode
}

export function CommandHeader({
  children,
  className,
  endAdornment,
  startAdornment,
  ...props
}: CommandHeaderProps) {
  return (
    <header
      className={cn(
        'flex min-h-12 items-center gap-x-2 border-b px-2 py-2',
        className,
      )}
      {...props}
    >
      {startAdornment}
      {typeof children === 'string' ? (
        <CommandHeaderContent className="text-tremor-content text-sm font-medium">
          {children}
        </CommandHeaderContent>
      ) : (
        children
      )}
      {endAdornment}
    </header>
  )
}

export type CommandFooterProps = ComponentProps<'footer'>

export function CommandFooter({ className, ...props }: CommandFooterProps) {
  return <footer className={cn('border-t p-2', className)} {...props} />
}

export interface CommandInputProps
  extends
    Omit<ComponentProps<typeof CommandPrimitive.Input>, 'size'>,
    Pick<
      VariantProps<typeof inputVariants>,
      'focusVariant' | 'roundingVariant' | 'size'
    > {
  endAdornment?: ReactNode
  startAdornment?: ReactNode

  /**
   * @default <SearchIcon />
   */
  startIcon?: ReactElement<unknown> | null
}

const defaultStartIcon = <SearchIcon />

export const CommandPrimitiveInput = CommandPrimitive.Input

export function CommandInput({
  className,
  endAdornment,
  focusVariant = 'none',
  roundingVariant = 'top',
  size,
  startAdornment,
  startIcon = defaultStartIcon,
  ...props
}: CommandInputProps) {
  const classNames = inputVariants({
    focusVariant,
    roundingVariant,
    size,
  })

  return (
    <div
      className={cn(
        classNames.base(),
        'border-b',
        startAdornment ? 'pr-3 pl-2' : 'px-3',
        className,
      )}
      cmdk-input-wrapper=""
    >
      {startAdornment}
      {startAdornment ? <Separator variant="vertical" /> : null}
      {startIcon ? (
        <Slot className={cn(classNames.icon(), 'opacity-50')}>{startIcon}</Slot>
      ) : null}
      <CommandPrimitiveInput className={classNames.input()} {...props} />
      {endAdornment}
    </div>
  )
}

export type CommandListProps = ComponentProps<typeof CommandPrimitive.List>

export function CommandList({ className, ...props }: CommandListProps) {
  return (
    <CommandPrimitive.List
      className={cn(
        'scrollable-y max-h-80 min-h-0 focus:outline-hidden',
        className,
      )}
      {...props}
    />
  )
}

export type CommandEmptyProps = ComponentProps<typeof CommandPrimitive.Empty>

export function CommandEmpty({ className, ...props }: CommandEmptyProps) {
  return (
    <CommandPrimitive.Empty
      className={cn('text-tremor-content py-6 text-center text-sm', className)}
      {...props}
    />
  )
}

export type CommandLoadingProps = ComponentProps<
  typeof CommandPrimitive.Loading
>

export function CommandLoading(props: CommandLoadingProps) {
  return (
    <CommandPrimitive.Loading
      className="text-tremor-content py-6 text-center text-sm"
      {...props}
    />
  )
}

export type CommandGroupProps = ComponentProps<typeof CommandPrimitive.Group>

export function CommandGroup({ className, ...props }: CommandGroupProps) {
  const groupScope = React.useId()

  return (
    <CommandGroupScopeContext value={groupScope}>
      <CommandPrimitive.Group
        className={cn(
          'text-foreground **:[[cmdk-group-heading]]:text-tremor-content overflow-hidden p-1 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium',
          className,
        )}
        {...props}
      />
    </CommandGroupScopeContext>
  )
}

export function CommandSeparator({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      className={cn('bg-tremor-border -mx-1 my-1 h-px', className)}
      {...props}
    />
  )
}

export interface CommandItemProps
  extends
    ComponentProps<typeof CommandPrimitive.Item>,
    Pick<DropdownMenuItemProps, 'animation' | 'size' | 'subtitle'> {
  containerClassName?: string
  endAdornment?: ReactElement<unknown>
  /**
   * @default false
   */
  isSelected?: boolean

  startAdornment?: ReactNode
  startIcon?: ReactElement
}

export function CommandItem({
  animation,
  children,
  className,
  containerClassName,
  endAdornment,
  isSelected,
  size,
  startAdornment,
  startIcon,
  subtitle,
  ...props
}: CommandItemProps) {
  const groupScope = React.use(CommandGroupScopeContext)
  const classNames = itemVariants({
    animation,
    isInteractive: true,
    size,
  })
  const itemValue =
    props.value ?? (typeof children === 'string' ? children : undefined)
  const scopedItemValue =
    groupScope !== null && itemValue !== undefined
      ? `${itemValue}${commandItemScopeSeparator}${groupScope}`
      : itemValue

  return (
    <CommandPrimitive.Item
      className={cn(classNames.base(), className)}
      {...props}
      value={scopedItemValue}
    >
      {startAdornment ? (
        <span className="mr-2 flex size-5 shrink-0 items-center justify-center text-xl">
          {startAdornment}
        </span>
      ) : startIcon ? (
        <Slot className={classNames.icon()}>{startIcon}</Slot>
      ) : null}

      <div className={cn(classNames.container(), containerClassName)}>
        {children}
        {subtitle === undefined ? null : typeof subtitle === 'string' ? (
          <Ellipsis asChild numLines={2}>
            <p className={classNames.subtitle()}>{subtitle}</p>
          </Ellipsis>
        ) : (
          subtitle
        )}
      </div>

      {endAdornment ? (
        <span className="ml-2 shrink-0">{endAdornment}</span>
      ) : null}

      {typeof isSelected === 'boolean' ? (
        <CheckIcon
          className={cn(
            'size-4 shrink-0 stroke-2',
            isSelected ? 'opacity-100' : 'opacity-0',
          )}
        />
      ) : null}
    </CommandPrimitive.Item>
  )
}

export { defaultCommandFilter, useCommandState }
